import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Flask, TestTube, Package, Truck, ShieldCheck,
  ArrowRight, ArrowUpRight, Check, CaretRight,
} from '@phosphor-icons/react';
import { HaleWordmark } from '../components/HaleBrand';
import TI from '../components/brand/TI';
import { useAuth } from '../contexts/AuthContext';

/* ====================================================================
 * /hale + "/" (Hale deployment) — The Hale League landing
 *
 * Distinct visual identity from ToxIntel / TAGC:
 *   - Modern grotesque sans (Chivo) display type, NOT editorial serif
 *   - White + light-neutral + charcoal/black bands (no cream canvas)
 *   - Hale red (#E63946) as the primary brand accent
 *   - ToxIntel pink used only sparingly as the scientific-review partner
 *   - Marketplace/partner-network layout: partner stack, cards, steps
 *
 * HALE is the primary brand. ToxIntel + The Agyekum Group appear only as
 * the scientific review partner. No backend dependency. Routes/CTAs
 * preserved (/hale/apply, /hale/dashboard, /login).
 * ==================================================================== */

const WHITE   = '#FFFFFF';
const NEUTRAL = '#F4F4F5';
const INK     = '#111214';
const INK_2   = '#1C1D21';
const SOFT     = '#3F3F46';
const MUTED   = '#71717A';
const LINE    = '#E4E4E7';
const RED      = '#E63946';
const RED_DEEP = '#C9192C';
const PINK     = '#FF4E6A';

const DISPLAY = "'Chivo', system-ui, -apple-system, sans-serif";
const BODY    = "'IBM Plex Sans', system-ui, -apple-system, sans-serif";
const MONO    = "'IBM Plex Mono', ui-monospace, monospace";

const PARTNERS = [
  { icon: Flask,       title: 'Formulation', desc: 'Product development partners who help you build standout men’s personal care.' },
  { icon: TestTube,    title: 'Testing', desc: 'Analytical and safety testing labs, ready at preferred League rates.' },
  { icon: Package,     title: 'Packaging', desc: 'Primary, secondary, and sustainable formats from vetted suppliers.' },
  { icon: Truck,       title: 'Fulfillment', desc: 'Logistics and fulfillment partners built to scale with your brand.' },
  { icon: ShieldCheck, title: 'Scientific Review', desc: 'Exposure-based safety and regulatory review by ToxIntel + A\u1d33.', partner: true },
];

const STEPS = [
  { n: '01', title: 'Apply / Intake', desc: 'Tell us about your brand, your products, and where you’re headed.' },
  { n: '02', title: 'Hale reviews brand fit', desc: 'We review quality, positioning, and growth stage against the League standard.' },
  { n: '03', title: 'Partner pathway selected', desc: 'We match you to the right partners for your next step — at preferred rates.' },
  { n: '04', title: 'Scientific / regulatory review', desc: 'ToxIntel + A\u1d33 run exposure-based safety and documentation review where needed.' },
  { n: '05', title: 'Move forward', desc: 'Grow with trusted partners and preferred workflows already in place.' },
];

const WHY = [
  { k: 'Curated brands', d: 'We work with the brands setting the quality standard for men’s personal care — not everyone.' },
  { k: 'Preferred rates', d: 'Established relationships with the best partners in each category, with preferred rates already in place.' },
  { k: 'Aligned incentives', d: 'When Hale brands grow, Hale grows. So Hale made it easy.' },
];

const btnBase = 'inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 text-[13px] font-semibold tracking-wide rounded-lg transition-transform duration-150 hover:-translate-y-0.5';

export default function HaleLandingPage() {
  // Active Hale session → route straight into the right surface (the
  // landing/marketing page is for signed-out visitors). Hale staff →
  // Hale Console; internal admins → ToxIntel Assessor Center; brands →
  // their brand workspace.
  const { user, loading } = useAuth();
  if (!loading && user) {
    const dest = user.role === 'hale_admin'
      ? '/hale/console'
      : user.role === 'admin'
        ? '/toxintel/assessor/hale'
        : '/hale/dashboard';
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: WHITE, color: INK, fontFamily: BODY }}>
      <Helmet>
        <title>The Hale League — The partner ecosystem for men’s personal care brands</title>
        <meta name="description" content="The Hale League gives men's personal care brands the partners they need to grow — formulation, testing, packaging, fulfillment, and scientific review, with preferred rates in place. Scientific review by ToxIntel + The Agyekum Group." />
        <link rel="icon" type="image/svg+xml" href="/hale-favicon.svg" />
        <link rel="icon" type="image/png" sizes="512x512" href="/hale-icon-512.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/hale-icon-192.png" />
        <link rel="shortcut icon" href="/hale-favicon.ico" />
        <link rel="apple-touch-icon" href="/hale-apple-touch-icon.png" />
        <meta name="theme-color" content="#E63946" />
      </Helmet>

      {/* ============================ HEADER ============================ */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${LINE}` }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="hale-brand-mark">
            <Link to="/" aria-label="Hale home" data-testid="hale-brand-mark-hale">
              <HaleWordmark size="sm" />
            </Link>
            <span className="hidden lg:flex items-center gap-1.5 pl-3 ml-1 border-l" style={{ borderColor: LINE }}>
              <span className="text-[9px] uppercase tracking-[0.18em]" style={{ color: MUTED, fontFamily: MONO }}>Scientific review partner</span>
              <span className="text-[12px]" style={{ color: INK }}><TI color={PINK} /></span>
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-7">
            <a href="#why" className="text-[12px] font-medium" style={{ color: SOFT }} data-testid="hale-nav-why">Why Hale</a>
            <a href="#partner-network" className="text-[12px] font-medium" style={{ color: SOFT }} data-testid="hale-nav-network">Partner Network</a>
            <a href="#how" className="text-[12px] font-medium" style={{ color: SOFT }} data-testid="hale-nav-how">How It Works</a>
            <a href="#science" className="text-[12px] font-medium" style={{ color: SOFT }} data-testid="hale-nav-science">Scientific Review</a>
            <Link to="/hale/login" className="text-[12px] font-medium" style={{ color: INK }} data-testid="hale-nav-signin">Brand Sign In</Link>
          </nav>

          <Link
            to="/hale/apply"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold"
            style={{ backgroundColor: RED, color: WHITE }}
            data-testid="hale-header-apply"
          >
            Apply <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </header>

      <main>
        {/* ============================ HERO ============================ */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-12 sm:pt-16 lg:pt-20 pb-14 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ backgroundColor: NEUTRAL }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RED }} />
                <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: SOFT, fontFamily: MONO }} data-testid="hale-hero-eyebrow">
                  The Hale League · Men&apos;s personal care ecosystem
                </span>
              </div>
              <h1
                className="font-black leading-[1.04] tracking-[-0.02em] text-[clamp(2rem,5.2vw,3.6rem)] mb-5"
                style={{ fontFamily: DISPLAY, color: INK }}
                data-testid="hale-hero-title"
              >
                The Hale League gives men&apos;s personal care brands the{' '}
                <span style={{ color: RED }}>partners they need to grow.</span>
              </h1>
              <p className="text-[15px] sm:text-base leading-relaxed max-w-xl mb-4" style={{ color: SOFT }} data-testid="hale-hero-sub">
                Hale curates high-quality men&apos;s personal care brands and connects them with trusted
                partners across formulation, testing, packaging, fulfillment, and scientific review.
              </p>
              <p className="text-[13px] mb-8" style={{ color: MUTED }}>
                Preferred partner rates already in place. Scientific review by <TI color={PINK} /> + A&#x1d33;.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/hale/apply" className={btnBase} style={{ backgroundColor: RED, color: WHITE }} data-testid="hale-cta-apply"
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_DEEP)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}>
                  Apply to the Hale League <ArrowUpRight size={15} weight="bold" />
                </Link>
                <a href="#partner-network" className={btnBase} style={{ backgroundColor: INK, color: WHITE }} data-testid="hale-cta-network">
                  Explore Partner Network
                </a>
                <Link to="/hale/login" className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 text-[13px] font-semibold tracking-wide rounded-lg border transition-colors" style={{ borderColor: LINE, color: INK, backgroundColor: WHITE }} data-testid="hale-cta-signin">
                  Brand Sign In <CaretRight size={13} weight="bold" />
                </Link>
              </div>
            </div>

            {/* Right — partner stack card */}
            <div className="lg:pl-4">
              <div className="rounded-2xl p-6 sm:p-7 shadow-xl" style={{ backgroundColor: INK, color: WHITE }} data-testid="hale-partner-stack">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#A1A1AA', fontFamily: MONO }}>Your partner stack</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded" style={{ backgroundColor: RED, color: WHITE, fontFamily: MONO }}>The Hale League</span>
                </div>
                <div className="space-y-2.5">
                  {PARTNERS.map((p) => (
                    <div key={p.title} className="flex items-center gap-3 rounded-xl px-4 py-3.5" style={{ backgroundColor: INK_2 }}>
                      <span className="flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0" style={{ backgroundColor: p.partner ? 'rgba(255,78,106,0.16)' : 'rgba(230,57,70,0.16)' }}>
                        <p.icon size={18} weight="bold" style={{ color: p.partner ? PINK : RED }} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold leading-tight" style={{ fontFamily: DISPLAY }}>{p.title}</p>
                        <p className="text-[11px] truncate" style={{ color: '#A1A1AA' }}>{p.partner ? 'ToxIntel + A\u1d33' : 'Preferred Hale partners'}</p>
                      </div>
                      <Check size={16} weight="bold" style={{ color: '#52525B', marginLeft: 'auto' }} className="flex-shrink-0" />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] mt-5 pt-4 border-t" style={{ borderColor: '#27272A', color: '#A1A1AA' }}>
                  Preferred rates already in place across every category.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ WHY HALE ============================ */}
        <section id="why" className="border-t" style={{ borderColor: LINE, backgroundColor: NEUTRAL }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <p className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: RED, fontFamily: MONO }}>Why Hale</p>
                <h2 className="font-black tracking-[-0.02em] leading-[1.08] text-[clamp(1.7rem,3.4vw,2.5rem)] mb-4" style={{ fontFamily: DISPLAY, color: INK }} data-testid="hale-why-title">
                  Brands need the right partners earlier.
                </h2>
                <p className="text-[15px] leading-relaxed" style={{ color: SOFT }}>
                  Hale is building the ecosystem for men&apos;s personal care — built around the brands setting the
                  quality standard for the industry. The focus is simple: curate the best products on the market and
                  make sure the brands behind them have what they need to grow.
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
                {WHY.map((w, i) => (
                  <div key={w.k} className="rounded-xl bg-white p-6 border" style={{ borderColor: LINE }} data-testid={`hale-why-${i}`}>
                    <span className="text-[12px] font-bold" style={{ color: RED, fontFamily: MONO }}>0{i + 1}</span>
                    <p className="text-[15px] font-bold mt-3 mb-2" style={{ fontFamily: DISPLAY, color: INK }}>{w.k}</p>
                    <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>{w.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ====================== THE PARTNER NETWORK ====================== */}
        <section id="partner-network" className="border-t" style={{ borderColor: LINE, backgroundColor: WHITE }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: RED, fontFamily: MONO }}>The Partner Network</p>
                <h2 className="font-black tracking-[-0.02em] leading-[1.08] text-[clamp(1.7rem,3.4vw,2.5rem)]" style={{ fontFamily: DISPLAY, color: INK }} data-testid="hale-network-title">
                  Everything a growing brand needs, in one network.
                </h2>
              </div>
              <Link to="/hale/apply" className="inline-flex items-center gap-1.5 text-[13px] font-semibold flex-shrink-0" style={{ color: RED }} data-testid="hale-network-apply">
                Request access <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {PARTNERS.map((p) => (
                <div
                  key={p.title}
                  className="rounded-xl p-6 border transition-shadow duration-150 hover:shadow-md"
                  style={{ borderColor: LINE, backgroundColor: p.partner ? '#FFF5F7' : WHITE }}
                  data-testid={`hale-partner-${p.title.toLowerCase().split(' ')[0]}`}
                >
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl mb-4" style={{ backgroundColor: p.partner ? 'rgba(255,78,106,0.12)' : 'rgba(230,57,70,0.10)' }}>
                    <p.icon size={22} weight="bold" style={{ color: p.partner ? PINK : RED }} />
                  </span>
                  <p className="text-[15px] font-bold mb-2" style={{ fontFamily: DISPLAY, color: INK }}>{p.title}</p>
                  <p className="text-[12.5px] leading-relaxed" style={{ color: MUTED }}>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ==================== HOW THE HALE LEAGUE WORKS ==================== */}
        <section id="how" style={{ backgroundColor: INK, color: WHITE }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
            <p className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: RED, fontFamily: MONO }}>How The Hale League Works</p>
            <h2 className="font-black tracking-[-0.02em] leading-[1.08] text-[clamp(1.7rem,3.4vw,2.5rem)] mb-12 max-w-2xl" style={{ fontFamily: DISPLAY, color: WHITE }} data-testid="hale-how-title">
              A clear path from apply to grow.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-px rounded-2xl overflow-hidden" style={{ backgroundColor: '#27272A' }}>
              {STEPS.map((s) => (
                <div key={s.n} className="p-6" style={{ backgroundColor: INK_2 }} data-testid={`hale-step-${s.n}`}>
                  <span className="text-[20px] font-black" style={{ fontFamily: DISPLAY, color: RED }}>{s.n}</span>
                  <p className="text-[14px] font-bold mt-3 mb-2 leading-tight" style={{ fontFamily: DISPLAY, color: WHITE }}>{s.title}</p>
                  <p className="text-[12px] leading-relaxed" style={{ color: '#A1A1AA' }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SCIENTIFIC REVIEW (secondary partner band) ============ */}
        <section id="science" className="border-t" style={{ borderColor: LINE, backgroundColor: WHITE }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 sm:py-16">
            <div className="rounded-2xl p-7 sm:p-9 border" style={{ borderColor: LINE, backgroundColor: '#FAFAFA' }}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} weight="bold" style={{ color: PINK }} />
                    <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: MUTED, fontFamily: MONO }}>Scientific review partner</span>
                  </div>
                  <h2 className="font-black tracking-[-0.02em] leading-[1.1] text-[clamp(1.4rem,2.6vw,2rem)] mb-3" style={{ fontFamily: DISPLAY, color: INK }} data-testid="hale-science-title">
                    Safety &amp; regulatory review by <TI color={PINK} /> + A&#x1d33;.
                  </h2>
                  <p className="text-[14px] leading-relaxed max-w-2xl" style={{ color: SOFT }}>
                    Where products need it, <TI color={PINK} /> — powered by The Agyekum Group — provides
                    exposure-based safety assessment, ingredient and claims review, and regulatory documentation aligned
                    to MoCRA, the EU Cosmetics Regulation, IFRA, and Prop&nbsp;65 — built for retailer-readiness.
                    Hale remains your commercial and program partner.
                  </p>
                </div>
                <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end">
                  {['MoCRA', 'EU 1223', 'IFRA', 'Prop 65', 'Retailer-ready'].map((t) => (
                    <span key={t} className="text-[11px] font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: LINE, color: SOFT, backgroundColor: WHITE }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ CTA BAND ============================ */}
        <section style={{ backgroundColor: RED }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 sm:py-16 text-center">
            <h2 className="font-black tracking-[-0.02em] leading-[1.08] text-[clamp(1.8rem,4vw,2.75rem)] mb-3" style={{ fontFamily: DISPLAY, color: WHITE }} data-testid="hale-cta-band-title">
              When Hale brands grow, Hale grows.
            </h2>
            <p className="text-[15px] mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>So Hale made it easy. Apply to bring your brand into the League.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/hale/apply" className={btnBase} style={{ backgroundColor: WHITE, color: INK }} data-testid="hale-cta-band-apply">
                Apply to the Hale League <ArrowUpRight size={15} weight="bold" />
              </Link>
              <Link to="/hale/login" className={btnBase} style={{ backgroundColor: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.6)' }} data-testid="hale-cta-band-dashboard">
                Access Brand Dashboard
              </Link>
            </div>
          </div>
        </section>

        {/* ============================ FOOTER ============================ */}
        <footer style={{ backgroundColor: INK, color: WHITE }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="flex items-center gap-3">
              <HaleWordmark size="sm" />
              <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#A1A1AA', fontFamily: MONO }}>The Hale League</span>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#A1A1AA', fontFamily: MONO }}>
              Scientific review partner · <TI color={PINK} /> · The Agyekum Group
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
